import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaMapMarkerAlt, FaBook, FaLaptop, FaPalette } from "react-icons/fa";
import "./StateSyllabus.css";
import "./DegreeCollege.css";
import { PU_ENQUIRY_URL } from "../config";

const DegreeCollege = () => {
  const highlights = [
    {
      icon: "🏛️",
      title: "Available Since",
      value: "2018",
      description: "Sri Vani Degree College established in 2018",
    },
    {
      icon: "🎓",
      title: "Affiliated To",
      value: "Bangalore University",
      description: "Affiliated to Bangalore University",
    },
    {
      icon: "📚",
      title: "Programs",
      value: "9 Courses",
      description: "B.Com, BBA, BCA, B.Sc, BA and more",
    },
    {
      icon: "🏫",
      title: "Campus",
      value: "Machohalli",
      description: "Available at Machohalli Campus with transport",
    },
  ];

  const programGroups = [
    {
      category: "Commerce & Management",
      icon: <FaBook />,
      color: "commerce",
      programs: [
        { name: "B.Com", full: "Bachelor of Commerce" },
        { name: "BBA", full: "Bachelor of Business Administration" },
      ],
    },
    {
      category: "Computer & Science",
      icon: <FaLaptop />,
      color: "science",
      programs: [
        { name: "BCA", full: "Bachelor of Computer Applications" },
        { name: "B.Sc (PCM)", full: "Physics, Chemistry & Mathematics" },
        { name: "B.Sc (PMCs)", full: "Physics, Mathematics & Computer Science" },
      ],
    },
    {
      category: "Arts & Humanities",
      icon: <FaPalette />,
      color: "arts",
      programs: [
        { name: "BA (HEP)", full: "History, Economics & Political Science" },
        { name: "BA — Dance", full: "Bachelor of Arts in Dance" },
        { name: "BA — Drama", full: "Bachelor of Arts in Drama" },
        { name: "BA — Music", full: "Bachelor of Arts in Music" },
      ],
    },
  ];

  const campus = {
    name: "Machohalli Campus",
    subtitle: "Macholli",
    tagline: "A Premier Centre of Academic Excellence & Innovation.",
    image: "/images/pu/pu-campus.jpg",
    link: "/campuses/macholli",
  };

  return (
    <div className="state-syllabus-page">
      {/* Page Header */}
      <section
        className="ss-page-header"
        style={{ backgroundImage: "url('/images/degree/degree-hero.png')" }}
      >
        <div className="ss-header-overlay" />
        <div className="container">
          <motion.div
            className="ss-header-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="ss-header-tag">Affiliated to Bangalore University</p>
            <h1>Degree College</h1>
            <p className="ss-header-sub">Sri Vani Vidya Kendra Degree College</p>
          </motion.div>
        </div>
      </section>

      {/* Quick Highlights */}
      <section className="ss-highlights-section">
        <div className="container">
          <div className="ss-highlights-grid">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                className="ss-highlight-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="ss-highlight-icon">{item.icon}</div>
                <div className="ss-highlight-value">{item.value}</div>
                <div className="ss-highlight-title">{item.title}</div>
                <div className="ss-highlight-desc">{item.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emblem + Bullets Section */}
      <section className="ss-emblem-section">
        <div className="container">
          <div className="ss-emblem-row">
            <motion.div
              className="ss-emblem-col"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/images/degree/bangalore-university.png"
                alt="Bangalore University"
                className="ss-emblem-img"
              />
            </motion.div>
            <motion.div
              className="ss-bullets-col"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <ul className="ss-key-bullets">
                <li>Available at Sri Vani Since 2018</li>
                <li>Affiliated to Bangalore University</li>
                <li>9 Undergraduate Degree Programs</li>
                <li>Industrial Visits &amp; Guest Lectures</li>
                <li>Pursue at Machohalli Campus</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="ss-description-section">
        <div className="container">
          <motion.div
            className="ss-description-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p>
              Graduation is a big step for students — a major milestone for many
              who step out into the world to build their careers and make a mark.
              Sri Vani Degree College offers affordable, world-class education
              through a multidisciplinary approach.
            </p>
            <p>
              The college enriches student life through industrial visits, guest
              lectures, seminars, workshops, personality development programs,
              and educational trips — preparing graduates for the demands of the
              modern professional world.
            </p>
            <div className="ss-desc-cta">
              <a
                href={PU_ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="ss-subjects-section section">
        <div className="container">
          <motion.div
            className="ss-subjects-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="ss-section-title">Graduate Degree Pathways</h2>
            <p className="ss-section-subtitle">
              9 undergraduate programs across Commerce, Science &amp; Arts
            </p>
          </motion.div>

          <div className="degree-programs-grid">
            {programGroups.map((group, index) => (
              <motion.div
                key={index}
                className="degree-group-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
              >
                <div className={`degree-group-header ${group.color}`}>
                  {group.icon}
                  <h3>{group.category}</h3>
                </div>
                <ul className="degree-program-list">
                  {group.programs.map((prog, i) => (
                    <li key={i}>
                      <span className="degree-prog-name">{prog.name}</span>
                      <span className="degree-prog-full">{prog.full}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Campus Section */}
      <section className="ss-campuses-section section">
        <div className="container">
          <motion.div
            className="ss-campuses-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="ss-section-tag">
              <FaMapMarkerAlt /> Pursue at Machohalli Campus
            </div>
            <h2 className="ss-section-title">Our Degree College Campus</h2>
          </motion.div>

          <div className="cbse-campus-center">
            <motion.div
              className="ss-campus-card cbse-single-campus"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="ss-campus-image">
                <img src={campus.image} alt={campus.name} />
                <div className="ss-campus-overlay">
                  <span>{campus.subtitle}</span>
                </div>
              </div>
              <div className="ss-campus-info">
                <h3>{campus.name}</h3>
                <p>{campus.tagline}</p>
                <Link to={campus.link} className="btn btn-outline ss-explore-btn">
                  Explore Campus
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ss-cta-section">
        <div className="container">
          <motion.div
            className="ss-cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Begin Your Degree Journey at Sri Vani</h2>
            <p>
              Affordable, world-class undergraduate education with a
              multidisciplinary approach for the modern professional world.
            </p>
            <div className="ss-cta-buttons">
              <a
                href={PU_ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply for Admission
              </a>
              <Link to="/contact" className="btn btn-outline-white">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DegreeCollege;
